import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { auth, db } from "../../firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import {
    Card,
    CardContent,
    Typography,
    Grid,
    TextField,
    Button,
    Box,
    Divider,
    InputAdornment,
} from "@mui/material";
import StoreIcon from "@mui/icons-material/Store";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";

export default function PropertyDetails() {
    const Toast = Swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
    });

    const [userData, setUserData] = useState(null);
    const [propertyData, setPropertyData] = useState({
        curshername: "",
        email: "",
        mobile: "",
        address: "",
        city: "",
        state: "",
        pincode: "",
        gstname: "",
        gstno: "",
        roomposting: "No",
        gstbill: "No",
        customerQr: "No",
        latitude: "",
        longitude: "",
        loginArea: 100,
    });

    const propertyDocId = "mjAl5ULD73FpSWvD8I0i"; // your single document ID

    // Get logged in user
    useEffect(() => {
        const unsub = auth.onAuthStateChanged((user) => {
            if (user) setUserData(user);
        });
        return () => unsub();
    }, []);

    // Fetch single property document
    useEffect(() => {
        if (!userData) return;

        const fetchProperty = async () => {
            try {
                const propertyRef = doc(db, "properties", propertyDocId);
                const propSnap = await getDoc(propertyRef);

                if (propSnap.exists()) {
                    const data = propSnap.data();
                    setPropertyData({
                        curshername: data.curshername || "",
                        email: data.email || userData.email || "",
                        mobile: data.mobile || "",
                        address: data.address || "",
                        city: data.city || "",
                        state: data.state || "",
                        pincode: data.pincode || "",
                        gstname: data.gstname || "",
                        gstno: data.gstno || "",
                        roomposting: data.roomposting || "No",
                        gstbill: data.gstbill || "No",
                        customerQr: data.customerQr || "No",
                        latitude: data.latitude || "",
                        longitude: data.longitude || "",
                        loginArea: data.loginArea || "",
                    });
                }
            } catch (err) {
                Toast.fire({ icon: "error", title: err.message });
            }
        };

        fetchProperty();
    }, [userData]);

    // Handle input changes
    const handleChange = (field, value) => {
        setPropertyData((prev) => ({ ...prev, [field]: value }));
    };

    // Save/update single document
    const handleUpdate = async () => {
        const { curshername, mobile } = propertyData;
        if (!curshername || mobile.length !== 10) {
            Toast.fire({ icon: "error", title: "Invalid input data" });
            return;
        }

        try {
            const ref = doc(db, "properties", propertyDocId);
            await updateDoc(ref, {
                ...propertyData,
                updatedAt: new Date(),
            });
            Toast.fire({ icon: "success", title: "Saved successfully" });
        } catch (err) {
            console.error(err);
            Toast.fire({ icon: "error", title: "Save failed" });
        }
    };

    // Update current location
    const handleUpdateLocation = async () => {
        if (!navigator.geolocation) {
            Toast.fire({ icon: "error", title: "Location not supported" });
            return;
        }

        try {
            const position = await new Promise((resolve, reject) =>
                navigator.geolocation.getCurrentPosition(resolve, reject, {
                    enableHighAccuracy: false,
                    timeout: 30000,
                    maximumAge: 60000,
                })
            );

            const lat = position.coords.latitude;
            const lng = position.coords.longitude;

            setPropertyData((prev) => ({ ...prev, latitude: lat, longitude: lng }));

            const ref = doc(db, "properties", propertyDocId);
            await updateDoc(ref, {
                latitude: lat,
                longitude: lng,
                geoRadius: 100,
                updatedAt: new Date(),
            });

            Toast.fire({ icon: "success", title: "Location Updated" });
        } catch (err) {
            console.error(err);
            Toast.fire({ icon: "error", title: "Location update failed" });
        }
    };

    return (
        <Box sx={{ p: 1 }}>
            <Card sx={{ mx: "auto", borderRadius: 4, boxShadow: "0 20px 40px rgba(0,0,0,0.15)" }}>
                <Box sx={{ p: 2 }}>
                    <Typography variant="h5" fontWeight="bold">
                        Property Details
                    </Typography>
                </Box>

                <CardContent>
                    <Grid className="row" spacing={3}>
                        {[
                            { label: "Crusher Name", field: "curshername", icon: <StoreIcon /> },
                            { label: "Email", field: "email", icon: <EmailIcon /> },
                            { label: "Mobile", field: "mobile", icon: <PhoneIcon /> },
                            { label: "Address", field: "address" },
                            { label: "City", field: "city" },
                            { label: "State", field: "state" },
                            { label: "Pincode", field: "pincode" },
                            { label: "GST Name", field: "gstname" },
                            { label: "GST No", field: "gstno" },
                            { label: "Latitude", field: "latitude" },
                            { label: "Longitude", field: "longitude" },
                            { label: "Login Area in Mtr", field: "loginArea" },
                        ].map((item) => (
                            <Grid className="col-sm-3 mb-3" key={item.field}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label={item.label}
                                    value={propertyData[item.field]}
                                    onChange={(e) => handleChange(item.field, e.target.value)}
                                    InputProps={item.icon ? { startAdornment: <InputAdornment position="start">{item.icon}</InputAdornment> } : undefined}
                                />
                            </Grid>
                        ))}
                    </Grid>

                    <Grid sx={{ flexDirection: "row", justifyContent: "space-between" }}>
                        <Button
                            size="large"
                            variant="contained"
                            onClick={handleUpdate} disabled
                            sx={{ px: 2, py: 1, fontSize: 16, borderRadius: 3, background: "linear-gradient(135deg, teal, #006c4f)" }}
                        >
                            Save Changes
                        </Button>

                        <Button
                            variant="outlined" disabled
                            onClick={handleUpdateLocation}
                            sx={{ px: 2, py: 1, borderRadius: 3, ml:5 }}
                        >
                            Update Property Location
                        </Button>
                    </Grid>


                    {propertyData.latitude && propertyData.longitude && (
                        <Card sx={{ mt: 4, borderRadius: 3 }}>
                            <CardContent>
                                <Typography variant="h6" fontWeight="bold" mb={2}>
                                    Property Location
                                </Typography>
                                <iframe
                                    width="100%"
                                    height="250"
                                    style={{ borderRadius: "12px" }}
                                    loading="lazy"
                                    allowFullScreen
                                    src={`https://www.google.com/maps?q=${propertyData.latitude},${propertyData.longitude}&z=17&output=embed`}
                                />
                                <Typography variant="body2" mt={1}>
                                    Lat: {propertyData.latitude} | Lng: {propertyData.longitude}
                                </Typography>
                            </CardContent>
                        </Card>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
}